import * as React from "react";
import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type { TodoDto } from "../../services/vorgangService";
import { useAuth } from "../../context/AuthContext";
import { StatusDropdown } from "./StatusDropdown";

const STATUS_CYCLE = ["Offen", "Erledigt"] as const;
export type TodoStatusValue = (typeof STATUS_CYCLE)[number];

// Einheitliche Farbkodierung wie StatusCell/StatusBadgeLarge: Offen = Aubergine,
// Erledigt = Grün (vorher färbte dieser Pfad „Offen" rot).
const statusVisual = (status: string): { bg: string; fg: string } => {
    switch (status) {
        case "Erledigt":
            return { bg: "#e7f6ec", fg: "#0e7c3a" };
        case "Offen":
        default:
            return { bg: "#f3ecf3", fg: "#6b2d5c" };
    }
};

/** Nur noch zwei Werte → der Klick auf den Chip schaltet um. */
const nextStatus = (current: string): TodoStatusValue =>
    current === "Erledigt" ? "Offen" : "Erledigt";

const formatDate = (iso?: string): string => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("de-DE");
};

const fristColor = (iso?: string): string => {
    if (!iso) return "#5a5a5a";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "#5a5a5a";
    const diff = d.getTime() - Date.now();
    if (diff < 0) return "#b91c1c";
    if (diff < 86400000 * 3) return "#a16207";
    return "#5a5a5a";
};

const useStyles = makeStyles({
    row: {
        display: "grid",
        gridTemplateColumns: "1fr 110px 110px 110px 36px",
        alignItems: "center",
        columnGap: "12px",
        padding: "10px 20px 10px 44px",
        borderBottom: "1px solid #e3e0e7",
        backgroundColor: "#ffffff",
        fontSize: "13px",
        cursor: "pointer",
        ":hover": { backgroundColor: "#f7f5fa" },
    },
    rowWithKunde: {
        gridTemplateColumns: "minmax(160px, 1.4fr) 150px 140px 110px 100px 90px 36px",
    },
    /** Ohne `onOpen` ist die Zeile reine Anzeige — kein Klick-Affordance. */
    rowStatic: {
        cursor: "default",
        ":hover": { backgroundColor: "#ffffff" },
    },
    ellipsis: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    title: {
        fontWeight: 500,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    statusChip: {
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 10px",
        borderRadius: "12px",
        fontSize: "11px",
        fontWeight: 600,
        whiteSpace: "nowrap",
        border: "none",
        cursor: "pointer",
        fontFamily: "inherit",
    },
    meta: {
        fontSize: "12px",
        color: "#6f6c75",
    },
    iconBtn: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "28px",
        height: "28px",
        border: "none",
        borderRadius: "6px",
        backgroundColor: "transparent",
        cursor: "pointer",
        color: "#0e7c3a",
        ":hover": { backgroundColor: "#e7f6ec" },
    },
});

export type TodoListRowProps = {
    todo: TodoDto;
    /**
     * Öffnet die Zeile. FEHLT der Callback, ist die Zeile bewusst nicht
     * anklickbar (kein Cursor/role="button") — der Status bleibt über das
     * Dropdown trotzdem änderbar.
     */
    onOpen?: (todo: TodoDto) => void;
    onStatusChange?: (todo: TodoDto, next: TodoStatusValue) => void;
    /** When provided, two extra leading columns (Kunde + Person) are shown. */
    kundeLabel?: string;
    personLabel?: string;
};

export const TodoListRow: React.FC<TodoListRowProps> = ({
    todo,
    onOpen,
    onStatusChange,
    kundeLabel,
    personLabel,
}) => {
    const styles = useStyles();
    const { user, isAdmin } = useAuth();
    const currentUserId: string | null = user?.id ?? null;
    const showColumns = kundeLabel !== undefined || personLabel !== undefined;
    const canChangeStatus =
        isAdmin ||
        (!!currentUserId && (todo.hauptverantwortlich ?? []).includes(currentUserId));

    const klickbar = !!onOpen;

    return (
        <div
            className={[
                styles.row,
                showColumns ? styles.rowWithKunde : "",
                klickbar ? "" : styles.rowStatic,
            ]
                .filter(Boolean)
                .join(" ")}
            onClick={klickbar ? () => onOpen?.(todo) : undefined}
            role={klickbar ? "button" : undefined}
            tabIndex={klickbar ? 0 : undefined}
            onKeyDown={
                klickbar
                    ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onOpen?.(todo);
                          }
                      }
                    : undefined
            }
        >
            <span className={styles.title}>{todo.titel}</span>
            {showColumns && (
                <>
                    <span className={mergeClasses(styles.meta, styles.ellipsis)} title={kundeLabel}>
                        {kundeLabel || "—"}
                    </span>
                    <span className={mergeClasses(styles.meta, styles.ellipsis)} title={personLabel}>
                        {personLabel || "—"}
                    </span>
                </>
            )}
            <span onClick={(e) => e.stopPropagation()}>
                <StatusDropdown
                    status={todo.status}
                    canChange={canChangeStatus}
                    onChange={(next) => onStatusChange?.(todo, next as TodoStatusValue)}
                    size="small"
                />
            </span>
            <span className={styles.meta} style={{ color: fristColor(todo.frist) }}>
                Frist: {formatDate(todo.frist)}
            </span>
            <span className={styles.meta}>TWV: {formatDate(todo.twv)}</span>
            {todo.status === "Erledigt" ? (
                <span style={{ color: "#0e7c3a", fontSize: 18, lineHeight: "28px", textAlign: "center" }}>
                    ✓
                </span>
            ) : (
                <span />
            )}
        </div>
    );
};

export { statusVisual, nextStatus, formatDate as formatTodoDate };
